import type { ViewColumnConfig } from '@features/views/models'
import type { Column } from '@features/columns/models'
import { getEffectivePinnedColumnIds } from './columnPinning'

/**
 * Reorder a list of columns according to their view column configs.
 * Mirrors the backend's `apply_column_configs()` in
 * `backend/app/db/query/queries.py` so the frontend's rendered order always
 * matches the order the backend returns in the records endpoint.
 *
 * Stable partition: columns with `is_pinned: true` move to the front in their
 * original relative order; everything else keeps its position behind them.
 *
 * @param validatedColumns  Columns in their natural (unpinned) order
 * @param columnConfigs     Sparse list of per-column config; only entries with
 *                          `is_pinned: true` cause a move
 */
export function applyColumnConfigs(
  validatedColumns: Column[],
  columnConfigs: ViewColumnConfig[] | undefined | null,
  defaultLabelColumnId?: string | null
): Column[] {
  if (validatedColumns.length === 0) {
    return validatedColumns
  }

  const pinnedIds = getEffectivePinnedColumnIds(
    validatedColumns,
    columnConfigs,
    defaultLabelColumnId
  )

  if (pinnedIds.size === 0) return validatedColumns

  const pinned = validatedColumns.filter((column) => pinnedIds.has(column.id))
  const unpinned = validatedColumns.filter((column) => !pinnedIds.has(column.id))

  return [...pinned, ...unpinned]
}
